import { useTranslation } from 'react-i18next';

import type { Incident } from '../../../api/types';
import { Button } from '../../../shared/ui/Button';
import { Card } from '../../../shared/ui/Card';
import { Spinner } from '../../../shared/ui/Spinner';
import styles from './IncidentDetails.module.css';

interface IncidentDetailsProps {
  incident: Incident | undefined;
  isLoading: boolean;
  isAcknowledging: boolean;
  onAcknowledge: (incidentId: number) => void;
}

// PARA-129, AC-16: read-only карточка — полей ввода здесь нет, редактирование
// записи не предусмотрено ни в API, ни в UI. Единственное действие — AC-17.
export function IncidentDetails({
  incident,
  isLoading,
  isAcknowledging,
  onAcknowledge,
}: IncidentDetailsProps): JSX.Element {
  const { t } = useTranslation();

  if (isLoading || !incident) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }

  const rows: Array<[string, string]> = [
    [t('monitoring.columns.severity'), t(`monitoring.severity.${incident.severity}`)],
    [t('monitoring.columns.category'), t(`monitoring.category.${incident.category}`)],
    [t('monitoring.columns.incidentType'), t(`monitoring.type.${incident.incident_type}`)],
    [t('monitoring.columns.occurredAt'), incident.occurred_at],
    [t('monitoring.columns.message'), incident.message],
    [t('monitoring.details.correlationId'), incident.correlation_id ?? '—'],
    [t('monitoring.details.requestMethod'), incident.request_method ?? '—'],
    [t('monitoring.details.requestPath'), incident.request_path ?? '—'],
    [t('monitoring.columns.subject'), incident.subject ?? '—'],
    [t('monitoring.details.clientIp'), incident.client_ip ?? '—'],
    [t('monitoring.details.userAgent'), incident.user_agent ?? '—'],
    [t('monitoring.details.source'), incident.source ?? '—'],
    [
      t('monitoring.details.acknowledgedBy'),
      incident.acknowledged_by === null ? '—' : String(incident.acknowledged_by),
    ],
    [t('monitoring.details.acknowledgedAt'), incident.acknowledged_at ?? '—'],
  ];

  return (
    <Card>
      <h2 className={styles.title}>{t('monitoring.details.title')}</h2>
      <dl className={styles.grid}>
        {rows.map(([label, value]) => (
          <div key={label} className={styles.row}>
            <dt className={styles.term}>{label}</dt>
            <dd className={styles.value}>{value}</dd>
          </div>
        ))}
      </dl>
      {incident.stack_trace ? (
        <pre className={styles.stackTrace}>{incident.stack_trace}</pre>
      ) : null}
      {incident.acknowledged ? null : (
        <Button
          variant="secondary"
          disabled={isAcknowledging}
          onClick={() => onAcknowledge(incident.id)}
        >
          {t('monitoring.acknowledge')}
        </Button>
      )}
    </Card>
  );
}
